<script setup>
// Editor de expediente semanal: um botao por dia da semana e, no dia ligado, o horario de inicio e de fim. O valor e a
// lista dos dias ligados: [{ day, startTime, endTime }]. Usado pela aba Profissionais e pela aba Agendas dos usuarios.
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { DAYS } from './constants';

const model = defineModel({ type: Array, default: () => [] });

const { t } = useI18n();

const DEFAULT_START = '08:00';
const DEFAULT_END = '18:00';

// Estado por dia (inclusive os desligados): ligar de novo um dia devolve o horario que estava digitado.
const build = (list, previous = {}) =>
  Object.fromEntries(
    DAYS.map(([, day]) => {
      const entry = list.find(item => item.day === day);
      return [
        day,
        {
          enabled: !!entry,
          startTime:
            entry?.startTime || previous[day]?.startTime || DEFAULT_START,
          endTime: entry?.endTime || previous[day]?.endTime || DEFAULT_END,
        },
      ];
    })
  );

const toList = state =>
  DAYS.filter(([, day]) => state[day].enabled).map(([, day]) => ({
    day,
    startTime: state[day].startTime,
    endTime: state[day].endTime,
  }));

const hours = ref(build(model.value));

watch(
  model,
  list => {
    if (JSON.stringify(toList(hours.value)) !== JSON.stringify(list)) {
      hours.value = build(list, hours.value);
    }
  },
  { deep: true }
);

watch(
  hours,
  state => {
    model.value = toList(state);
  },
  { deep: true }
);
</script>

<template>
  <div class="space-y-1.5" data-test="working-hours-editor">
    <div v-for="[lbl, d] in DAYS" :key="d" class="flex items-center gap-3">
      <button
        type="button"
        class="w-16 shrink-0 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors"
        :class="
          hours[d].enabled
            ? 'bg-n-brand text-white border-n-brand'
            : 'bg-n-background text-n-slate-11 border-n-weak hover:border-n-slate-6'
        "
        :data-test="`day-${d}`"
        @click="hours[d].enabled = !hours[d].enabled"
      >
        {{ t(lbl) }}
      </button>
      <div
        v-if="hours[d]?.enabled"
        class="flex items-center gap-2 flex-1 min-w-0"
      >
        <input
          v-model="hours[d].startTime"
          type="time"
          class="reset-base flex-1 min-w-0 h-8 px-2 text-xs rounded-lg outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-blue-9 bg-n-alpha-1 text-n-slate-12"
        /><span class="text-xs text-n-slate-10 shrink-0">{{
          t('ATENDIMENTOS.practitioners.modal.to')
        }}</span
        ><input
          v-model="hours[d].endTime"
          type="time"
          class="reset-base flex-1 min-w-0 h-8 px-2 text-xs rounded-lg outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-blue-9 bg-n-alpha-1 text-n-slate-12"
        />
      </div>
      <span v-else class="text-xs text-n-slate-9">{{
        t('ATENDIMENTOS.practitioners.modal.unavailable')
      }}</span>
    </div>
  </div>
</template>
